// profileUI.ts - Player profile UI extracted from main.js

import { getFactions } from './game.ts';
import { playerProfile } from './playerProfile.ts';
import type { PlayerFactionStatEntry, PlayerStatEntry } from './playerProfile.ts';
import { showError, hideError, setButtonLoading } from './uiUtils.ts';

/** profileUI stores the shared context blob but currently reads nothing from it. */
export type ProfileUIContext = object;

let _ctx: ProfileUIContext;

export function setup(context: ProfileUIContext): void {
    _ctx = context;
}

// Update the player profile bar in the menu header
export function updatePlayerProfileBar(): void {
    const nameEl = document.getElementById('player-profile-name');
    if (nameEl) {
        nameEl.textContent = playerProfile.displayName;
    }
    const btnEl = document.getElementById('btn-player-profile');
    if (btnEl) {
        btnEl.classList.toggle('unregistered-pulse', !playerProfile.isRegistered);
    }
}

export function setupPlayerProfileHandlers(): void {
    const modal = document.getElementById('player-profile-modal');
    const btnOpen = document.getElementById('btn-player-profile');
    const btnClose = document.getElementById('btn-close-profile');

    if (!modal || !btnOpen) return;

    // Open modal. Refresh session state first so the modal can decide whether
    // a registered player needs to re-recover their session.
    btnOpen.addEventListener('click', async () => {
        await playerProfile.refreshSessionState();
        refreshProfileModal();
        modal.classList.remove('hidden');
    });

    // Close modal
    if (btnClose) {
        btnClose.addEventListener('click', () => {
            modal.classList.add('hidden');
        });
    }
    modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.classList.add('hidden');
    });

    // Register callsign — server responds with the recovery code, which the user
    // must retype to confirm before the prompt is dismissed.
    const btnRegister = document.getElementById('btn-register-callsign') as HTMLButtonElement | null;
    if (btnRegister) {
        btnRegister.addEventListener('click', async () => {
            const input = document.getElementById('register-callsign') as HTMLInputElement;
            const errorEl = document.getElementById('register-error');
            const callsign = input.value.trim();

            if (!callsign || callsign.length < 2) {
                showError(errorEl, 'Callsign must be at least 2 characters');
                return;
            }

            const restore = setButtonLoading(btnRegister, 'REGISTERING...');
            const result = await playerProfile.register(callsign);
            restore();

            if (result.success) {
                hideError(errorEl);
                _pendingRecoveryCode = result.recoveryCode;
                document.getElementById('recovery-code-value')!.textContent = result.recoveryCode;
                document.getElementById('profile-recovery-display')!.classList.remove('hidden');
                const confirmInput = document.getElementById('recovery-code-confirm-input') as HTMLInputElement | null;
                if (confirmInput) {
                    confirmInput.value = '';
                    confirmInput.focus();
                }
                refreshProfileModal();
                updatePlayerProfileBar();
            } else {
                showError(errorEl, result.error);
            }
        });
    }

    // Confirm recovery code (retype-to-confirm). On match, hide the code and stash
    // the "you've saved it" acknowledgement for the rest of the session.
    const btnConfirmRecovery = document.getElementById('btn-confirm-recovery-code');
    if (btnConfirmRecovery) {
        btnConfirmRecovery.addEventListener('click', () => {
            const input = document.getElementById('recovery-code-confirm-input') as HTMLInputElement | null;
            const errorEl = document.getElementById('recovery-confirm-error');
            const successEl = document.getElementById('recovery-confirm-success');
            const typed = (input?.value || '').trim().toUpperCase();

            if (!_pendingRecoveryCode) return;

            if (typed !== _pendingRecoveryCode) {
                showError(errorEl, 'Codes do not match. Try again.');
                successEl?.classList.add('hidden');
                return;
            }

            hideError(errorEl);
            _pendingRecoveryCode = null;
            const display = document.getElementById('profile-recovery-display');
            if (display) display.classList.add('hidden');
            if (successEl) {
                successEl.textContent = 'Recovery code saved.';
                successEl.classList.remove('hidden');
            }
        });
    }

    // Recover profile (lost-everything fallback — callsign + recovery code)
    const btnRecover = document.getElementById('btn-recover-profile') as HTMLButtonElement | null;
    if (btnRecover) {
        btnRecover.addEventListener('click', async () => {
            const callsignInput = document.getElementById('recover-callsign') as HTMLInputElement;
            const codeInput = document.getElementById('recover-code') as HTMLInputElement;
            const errorEl = document.getElementById('recover-error');
            const successEl = document.getElementById('recover-success')!;

            const callsign = callsignInput.value.trim();
            const code = codeInput.value.trim().toUpperCase();

            if (!callsign || !code) {
                showError(errorEl, 'Both fields are required');
                successEl.classList.add('hidden');
                return;
            }

            const restore = setButtonLoading(btnRecover, 'RECOVERING...');
            const result = await playerProfile.recover(callsign, code);
            restore();

            if (result.success) {
                hideError(errorEl);
                successEl.textContent = 'Profile recovered successfully!';
                successEl.classList.remove('hidden');
                refreshProfileModal();
                updatePlayerProfileBar();
            } else {
                successEl.classList.add('hidden');
                showError(errorEl, result.error);
            }
        });
    }

    // Generate a pairing code (registered users → second device)
    const btnGenPair = document.getElementById('btn-generate-pairing-code') as HTMLButtonElement | null;
    if (btnGenPair) {
        btnGenPair.addEventListener('click', async () => {
            const errorEl = document.getElementById('pairing-code-error');
            const display = document.getElementById('pairing-code-display');
            const valueEl = document.getElementById('pairing-code-value');

            const restore = setButtonLoading(btnGenPair, 'GENERATING...');
            const result = await playerProfile.requestPairingCode();
            restore();

            if (!result.success) {
                if (display) display.classList.add('hidden');
                showError(errorEl, result.error);
                return;
            }

            hideError(errorEl);
            if (valueEl) valueEl.textContent = formatPairingCode(result.code);
            if (display) display.classList.remove('hidden');
            startPairingCountdown(result.expiresAt);
        });
    }

    // Pair this device (unregistered users → enter code from another device)
    const btnPair = document.getElementById('btn-pair-device') as HTMLButtonElement | null;
    if (btnPair) {
        btnPair.addEventListener('click', async () => {
            const input = document.getElementById('pair-code-input') as HTMLInputElement | null;
            const errorEl = document.getElementById('pair-error');
            const successEl = document.getElementById('pair-success');
            const code = (input?.value || '').replace(/\s+/g, '');

            if (!/^\d{6}$/.test(code)) {
                showError(errorEl, 'Enter the 6-digit code shown on your other device');
                successEl?.classList.add('hidden');
                return;
            }

            const restore = setButtonLoading(btnPair, 'SIGNING IN...');
            const result = await playerProfile.pairWithCode(code);
            restore();

            if (result.success) {
                hideError(errorEl);
                if (successEl) {
                    successEl.textContent = 'Signed in.';
                    successEl.classList.remove('hidden');
                }
                if (input) input.value = '';
                refreshProfileModal();
                updatePlayerProfileBar();
            } else {
                successEl?.classList.add('hidden');
                showError(errorEl, result.error);
            }
        });
    }
}

// State for the registration-time retype confirmation. Cleared after the user
// types the code back successfully (or refreshes the page).
let _pendingRecoveryCode: string | null = null;

// State for the pairing-code countdown timer.
let _pairingCountdownInterval: ReturnType<typeof setInterval> | null = null;

function formatPairingCode(code: string): string {
    // "123456" → "123 456" — easier to read aloud / type on another device.
    return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

function startPairingCountdown(expiresAt: number): void {
    const countdownEl = document.getElementById('pairing-code-countdown');
    const display = document.getElementById('pairing-code-display');
    if (!countdownEl) return;

    if (_pairingCountdownInterval) clearInterval(_pairingCountdownInterval);

    const tick = (): void => {
        const remainingMs = expiresAt - Date.now();
        if (remainingMs <= 0) {
            clearInterval(_pairingCountdownInterval!);
            _pairingCountdownInterval = null;
            if (display) display.classList.add('hidden');
            return;
        }
        const totalSec = Math.ceil(remainingMs / 1000);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        countdownEl.textContent = `${min}:${sec.toString().padStart(2, '0')}`;
    };

    tick();
    _pairingCountdownInterval = setInterval(tick, 1000);
}

function refreshProfileModal(): void {
    const registeredSection = document.getElementById('profile-registered')!;
    const unregisteredSection = document.getElementById('profile-unregistered')!;
    const callsignDisplay = document.getElementById('profile-callsign-display')!;
    const recoveryDisplay = document.getElementById('profile-recovery-display')!;
    const pairingSection = document.getElementById('profile-pairing-section');
    const pairSection = document.getElementById('profile-pair-section');
    const recoverSection = document.getElementById('profile-recover-section');
    const prePairDivider = document.getElementById('profile-pre-pair-divider');

    const sessionHint = document.getElementById('session-expired-hint');

    if (playerProfile.isRegistered) {
        registeredSection.classList.remove('hidden');
        unregisteredSection.classList.add('hidden');
        callsignDisplay.textContent = playerProfile.callsign;

        // Registered + cross-device pair-with-code form is always irrelevant
        // (registered users use the "pair another device" generator, not the
        // anonymous "I have a code" path), so its divider is irrelevant too.
        if (pairSection) pairSection.classList.add('hidden');
        if (prePairDivider) prePairDivider.classList.add('hidden');

        if (playerProfile.sessionBound) {
            // Session alive — show the pairing generator, hide the recover form.
            if (pairingSection) pairingSection.classList.remove('hidden');
            if (recoverSection) recoverSection.classList.add('hidden');
            if (sessionHint) sessionHint.classList.add('hidden');
        } else {
            // Session lost (server restart wiped it, or the cookie expired). The
            // pairing generator requires a session, so hide it and surface the
            // recover form with a hint banner.
            if (pairingSection) pairingSection.classList.add('hidden');
            if (recoverSection) recoverSection.classList.remove('hidden');
            if (sessionHint) sessionHint.classList.remove('hidden');
        }
        // Recovery display visibility is controlled by the registration handler.
    } else {
        registeredSection.classList.add('hidden');
        unregisteredSection.classList.remove('hidden');
        recoveryDisplay.classList.add('hidden');

        // Anonymous — hide the "pair another device" generator, show the entry forms.
        if (pairingSection) pairingSection.classList.add('hidden');
        if (pairSection) pairSection.classList.remove('hidden');
        if (recoverSection) recoverSection.classList.remove('hidden');
        if (prePairDivider) prePairDivider.classList.remove('hidden');
        if (sessionHint) sessionHint.classList.add('hidden');
    }

    renderProfileStats();
    renderProfileFactions();
    renderProfileTournaments();
}

/** Stat-box row item ({ value, label }); values render as-is. */
interface StatRowItem {
    value: string | number;
    label: string;
}

function renderProfileStats(): void {
    const section = document.getElementById('profile-stats-section');
    const summaryEl = document.getElementById('profile-stats-summary');
    const bestsEl = document.getElementById('profile-stats-bests');

    if (!section || !summaryEl || !bestsEl) return;

    const factionStats: PlayerFactionStatEntry[] = playerProfile.factionStats || [];
    const playerStats: PlayerStatEntry[] = playerProfile.stats || [];

    // Derive overview counts from factionStats (match history — always populated)
    const aggregateFromMatches = (mode: string): { games: number; wins: number; losses: number } => {
        const entries = factionStats.filter(fs => fs.mode === mode);
        let games = 0, wins = 0;
        for (const fs of entries) {
            games += fs.games;
            wins += fs.wins;
        }
        return { games, wins, losses: games - wins };
    };

    // Derive personal bests from playerStats (player_stats table — SP only, uses game-type modes)
    const aggregateBests = (): { bestScore: number; bestScoreFaction: string; bestTurns: number; bestTime: number } => {
        let bestScore = 0, bestScoreFaction = '', bestTurns = 0, bestTime = 0;
        for (const s of playerStats) {
            if (s.bestScore > bestScore) {
                bestScore = s.bestScore;
                bestScoreFaction = s.bestFaction || '';
            }
            if (s.bestTurns > 0 && (bestTurns === 0 || s.bestTurns < bestTurns)) {
                bestTurns = s.bestTurns;
            }
            if (s.bestTime > 0 && (bestTime === 0 || s.bestTime < bestTime)) {
                bestTime = s.bestTime;
            }
        }
        return { bestScore, bestScoreFaction, bestTurns, bestTime };
    };

    const spOverview = aggregateFromMatches('singleplayer');
    const mpOverview = aggregateFromMatches('multiplayer');
    const totalGames = spOverview.games + mpOverview.games;

    if (totalGames === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    const factions = getFactions();
    const formatTime = (seconds: number): string => {
        if (!seconds) return '--';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    // Helper: create a stat-box row
    const makeStatRow = (items: StatRowItem[], bestRow: boolean): HTMLElement => {
        const row = document.createElement('div');
        row.className = 'tournament-summary-row';
        items.forEach(s => {
            const box = document.createElement('div');
            box.className = 'tournament-stat-box';
            const val = document.createElement('span');
            val.className = 'tournament-stat-value' + (bestRow ? ' profile-stat-best-value' : '');
            // Assigned as-is (numbers coerce like the JS original did).
            val.textContent = s.value as string;
            const lbl = document.createElement('span');
            lbl.className = 'tournament-stat-label';
            lbl.textContent = s.label;
            box.appendChild(val);
            box.appendChild(lbl);
            row.appendChild(box);
        });
        return row;
    };

    // Helper: build a mode block (label + overview row + optional bests row)
    const buildModeBlock = (label: string, overview: { games: number; wins: number; losses: number }, showBests: boolean): DocumentFragment => {
        const block = document.createDocumentFragment();
        const heading = document.createElement('div');
        heading.className = 'profile-mode-label';
        heading.textContent = label;
        block.appendChild(heading);

        const winRate = overview.games > 0 ? Math.round((overview.wins / overview.games) * 100) : 0;
        block.appendChild(makeStatRow([
            { value: overview.games, label: 'Played' },
            { value: overview.wins, label: 'Wins' },
            { value: overview.losses, label: 'Losses' },
            { value: winRate + '%', label: 'Win Rate' }
        ], false));

        if (showBests) {
            const bests = aggregateBests();
            const fName = (factions && factions[bests.bestScoreFaction]) ? factions[bests.bestScoreFaction].name : bests.bestScoreFaction;
            block.appendChild(makeStatRow([
                { value: bests.bestScore || '--', label: 'Best Score' },
                { value: bests.bestTurns || '--', label: 'Fewest Turns' },
                { value: formatTime(bests.bestTime), label: 'Fastest Win' },
                { value: fName || '--', label: 'Best Faction' }
            ], true));
        }

        return block;
    };

    const frag = document.createDocumentFragment();

    if (spOverview.games > 0) {
        frag.appendChild(buildModeBlock('SOLO', spOverview, true));
    }
    if (mpOverview.games > 0) {
        frag.appendChild(buildModeBlock('MULTIPLAYER', mpOverview, false));
    }

    summaryEl.replaceChildren(frag);
    bestsEl.replaceChildren();
}

function renderProfileFactions(): void {
    const section = document.getElementById('profile-factions-section');
    const listEl = document.getElementById('profile-factions-list');

    if (!section || !listEl) return;

    const factionStats = playerProfile.factionStats || [];

    // Split by mode, each sorted by games desc (already sorted from server).
    // Other modes (e.g. roguelike) aren't shown, so they can't keep the section open.
    const spEntries = factionStats.filter(fs => fs.mode === 'singleplayer');
    const mpEntries = factionStats.filter(fs => fs.mode === 'multiplayer');
    if (spEntries.length === 0 && mpEntries.length === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    const factions = getFactions();

    // Find overall favorite (most total games across modes)
    const totalByFaction: Record<string, number> = {};
    for (const fs of factionStats) {
        totalByFaction[fs.faction] = (totalByFaction[fs.faction] || 0) + fs.games;
    }
    let favoriteFaction = '';
    let favoriteMax = 0;
    for (const [f, g] of Object.entries(totalByFaction)) {
        if (g > favoriteMax) { favoriteMax = g; favoriteFaction = f; }
    }

    const buildFactionRow = (fs: PlayerFactionStatEntry): HTMLElement => {
        const row = document.createElement('div');
        const isFav = fs.faction === favoriteFaction;
        row.className = 'profile-faction-row' + (isFav ? ' profile-faction-favorite' : '');

        const factionData = factions ? factions[fs.faction] : null;

        const logo = document.createElement('img');
        logo.className = 'profile-faction-logo';
        logo.src = factionData ? factionData.logo! : `/djbship/images/logos/${fs.faction}.svg`;
        logo.alt = fs.faction;
        logo.width = 24;
        logo.height = 24;
        row.appendChild(logo);

        const name = document.createElement('span');
        name.className = 'profile-faction-name';
        name.textContent = factionData ? factionData.name : fs.faction;
        row.appendChild(name);

        if (isFav) {
            const badge = document.createElement('span');
            badge.className = 'profile-faction-badge';
            badge.textContent = 'FAVORITE';
            row.appendChild(badge);
        }

        const gamesSpan = document.createElement('span');
        gamesSpan.className = 'profile-faction-games';
        gamesSpan.textContent = fs.games + (fs.games === 1 ? ' game' : ' games');
        row.appendChild(gamesSpan);

        const winRateSpan = document.createElement('span');
        winRateSpan.className = 'profile-faction-winrate';
        winRateSpan.textContent = fs.winRate + '%';
        row.appendChild(winRateSpan);

        return row;
    };

    const buildColumnHeader = (): HTMLElement => {
        const header = document.createElement('div');
        header.className = 'profile-faction-row profile-faction-header';
        // Spacer for logo column
        const logoSpacer = document.createElement('span');
        logoSpacer.className = 'profile-faction-logo-spacer';
        header.appendChild(logoSpacer);
        const nameLabel = document.createElement('span');
        nameLabel.className = 'profile-faction-name';
        nameLabel.textContent = 'Faction';
        header.appendChild(nameLabel);
        const gamesLabel = document.createElement('span');
        gamesLabel.className = 'profile-faction-games';
        gamesLabel.textContent = 'Games';
        header.appendChild(gamesLabel);
        const winLabel = document.createElement('span');
        winLabel.className = 'profile-faction-winrate';
        winLabel.textContent = 'Win %';
        header.appendChild(winLabel);
        return header;
    };

    const frag = document.createDocumentFragment();

    if (spEntries.length > 0) {
        const label = document.createElement('div');
        label.className = 'profile-mode-label';
        label.textContent = 'SOLO';
        frag.appendChild(label);
        frag.appendChild(buildColumnHeader());
        spEntries.forEach(fs => frag.appendChild(buildFactionRow(fs)));
    }

    if (mpEntries.length > 0) {
        const label = document.createElement('div');
        label.className = 'profile-mode-label';
        label.textContent = 'MULTIPLAYER';
        frag.appendChild(label);
        frag.appendChild(buildColumnHeader());
        mpEntries.forEach(fs => frag.appendChild(buildFactionRow(fs)));
    }

    listEl.replaceChildren(frag);
}

function renderProfileTournaments(): void {
    const section = document.getElementById('profile-tournament-section');
    const summaryEl = document.getElementById('profile-tournament-summary');
    const listEl = document.getElementById('profile-tournament-list');

    if (!section || !summaryEl || !listEl) return;

    const records = playerProfile.tournamentRecords;
    const summary = playerProfile.tournamentSummary!;

    if (!records || records.length === 0) {
        section.classList.add('hidden');
        return;
    }

    section.classList.remove('hidden');

    // Render summary row
    const summaryRow = document.createElement('div');
    summaryRow.className = 'tournament-summary-row';
    const statData: StatRowItem[] = [
        { value: summary.tournamentsEntered, label: 'Entered' },
        { value: summary.championships, label: 'Titles' },
        { value: summary.totalWins, label: 'Wins' },
        { value: summary.totalLosses, label: 'Losses' }
    ];
    statData.forEach(s => {
        const box = document.createElement('div');
        box.className = 'tournament-stat-box';
        const val = document.createElement('span');
        val.className = 'tournament-stat-value';
        // Assigned as-is (numbers coerce like the JS original did).
        val.textContent = s.value as string;
        const lbl = document.createElement('span');
        lbl.className = 'tournament-stat-label';
        lbl.textContent = s.label;
        box.appendChild(val);
        box.appendChild(lbl);
        summaryRow.appendChild(box);
    });
    summaryEl.replaceChildren(summaryRow);

    // Render record items
    const listFrag = document.createDocumentFragment();
    records.forEach(r => {
        const item = document.createElement('div');
        item.className = 'tournament-record-item';

        const name = document.createElement('span');
        name.className = 'tournament-record-name';
        name.textContent = r.tournamentName;

        const badge = document.createElement('span');
        badge.className = `tournament-status-badge tournament-status-${r.status}`;
        badge.textContent = r.status.charAt(0).toUpperCase() + r.status.slice(1);

        const wl = document.createElement('span');
        wl.className = 'tournament-record-wl';
        wl.textContent = `${r.wins}W-${r.losses}L`;

        item.appendChild(name);
        item.appendChild(badge);
        item.appendChild(wl);
        listFrag.appendChild(item);
    });
    listEl.replaceChildren(listFrag);
}
